import {
    ConfirmationDialogContext,
    ConfirmationDialogProperties,
} from '../../components/common/confirmation-dialog/contexts';
import { ConfigureRemoteDesktopDialogContext } from '../../components/dev-box/configure-remote-desktop-dialog/contexts';
import {
    DelayShutdownDialogContext,
    DelayShutdownDialogProperties,
} from '../../components/dev-box/delay-shutdown-dialog/contexts';
import {
    HibernatePreviewDialogContext,
    HibernatePreviewDialogProperties,
} from '../../components/dev-box/hibernate-preview-dialog/context';
import {
    OpenWindowsDesktopClientDialogContext,
    OpenWindowsDesktopClientDialogProperties,
} from '../../components/dev-box/open-windows-desktop-client-dialog/contexts';
import { RepairDialogContext, RepairDialogProperties } from '../../components/dev-box/repair-dialog/context';
import {
    ChangeEnvironmentExpirationDialogContext,
    ChangeEnvironmentExpirationDialogProperties,
} from '../../components/environment/change-expiration-dialog/contexts';
import {
    RedeployEnvironmentDialogContext,
    RedeployEnvironmentDialogProperties,
} from '../../components/environment/redeploy-environment-dialog/contexts';
import {
    ConnectViaAppDialogContext,
    OpenConnectViaAppDialogProperties,
} from '../../components/user-settings/connect-via-app-dialog/contexts';
import { DialogName } from '../../constants/app';
import { AsyncState } from '../../redux/store/common-state';
import {
    defaultCloseSurfaceHandler,
    defaultOpenSurfaceHandler,
    defaultOpenSurfaceHandlerWithProperties,
    surfaceContextHook,
    surfaceContextWithPropertiesHook,
} from './common';

const defaultCloseDialogHandler = defaultCloseSurfaceHandler;
const defaultOpenDialogHandler = defaultOpenSurfaceHandler;
const defaultOpenDialogHandlerWithProperties = defaultOpenSurfaceHandlerWithProperties;

const defaultOnSubmit = () => {
    return;
};

const defaultCloseConfigureRemoteDesktopDialogHandler = defaultCloseDialogHandler(DialogName.ConfigureRemoteDesktop);
const defaultOpenConfigureRemoteDesktopDialogHandler = defaultOpenDialogHandler(DialogName.ConfigureRemoteDesktop);
const defaultCloseConnectViaAppDialogHandler = defaultCloseDialogHandler(DialogName.ConnectViaApp);
const defaultOpenConnectViaAppDialogHandler = defaultOpenDialogHandler(DialogName.ConnectViaApp);
const defaultCloseDelayShutdownDialogHandler = defaultCloseDialogHandler(DialogName.DelayShutdown);
const defaultOpenDelayShutdownDialogHandler = defaultOpenDialogHandlerWithProperties(DialogName.DelayShutdown);
const defaultCloseOpenWindowsDesktopClientDialogHandler = defaultCloseDialogHandler(
    DialogName.OpenWindowsDesktopClient
);
const defaultOpenOpenWindowsDesktopClientDialogHandler = defaultOpenDialogHandler(DialogName.OpenWindowsDesktopClient);
const defaultCloseConfirmationDialogHandler = defaultCloseDialogHandler(DialogName.Confirmation);
const defaultOpenConfirmationDialogHandler = defaultOpenDialogHandlerWithProperties(DialogName.Confirmation);
const defaultCloseRedeployEnvironmentDialogHandler = defaultCloseDialogHandler(DialogName.RedeployEnvironment);
const defaultOpenRedeployEnvironmentDialogHandler = defaultOpenDialogHandlerWithProperties(
    DialogName.RedeployEnvironment
);
const defaultCloseHibernatePreviewDialogHandler = defaultCloseDialogHandler(DialogName.HibernatePreview);
const defaultOpenHibernatePreviewDialogHandler = defaultOpenDialogHandler(DialogName.HibernatePreview);
const defaultCloseRepairDialogHandler = defaultCloseDialogHandler(DialogName.Repair);
const defaultOpenRepairDialogHandler = defaultOpenDialogHandler(DialogName.Repair);
const defaultCloseChangeEnvironmentExpirationDialogHandler = defaultCloseDialogHandler(
    DialogName.ChangeEnvironmentExpiration
);
const defaultOpenChangeEnvironmentExpirationDialogHandler = defaultOpenDialogHandlerWithProperties(
    DialogName.ChangeEnvironmentExpiration
);

export const useConfigureRemoteDesktopDialogContext = surfaceContextHook(
    ConfigureRemoteDesktopDialogContext,
    defaultOpenConfigureRemoteDesktopDialogHandler,
    defaultCloseConfigureRemoteDesktopDialogHandler
);

export const useDelayShutdownDialogContext = surfaceContextWithPropertiesHook<DelayShutdownDialogProperties>(
    DelayShutdownDialogContext,
    defaultOpenDelayShutdownDialogHandler,
    defaultCloseDelayShutdownDialogHandler,
    {
        devBoxId: '',
        locale: '',
        originalScheduledTime: new Date(),
        onDelaySubmitted: defaultOnSubmit,
        onSkipSubmitted: defaultOnSubmit,
        hasHibernateSupport: true,
        delayState: AsyncState.NotStarted,
        skipState: AsyncState.NotStarted,
        nextScheduledTime: new Date(),
    }
);

export const useRedeployEnvironmentDialogContext =
    surfaceContextWithPropertiesHook<RedeployEnvironmentDialogProperties>(
        RedeployEnvironmentDialogContext,
        defaultOpenRedeployEnvironmentDialogHandler,
        defaultCloseRedeployEnvironmentDialogHandler,
        {
            environment: {
                environmentType: '',
                catalogName: '',
                environmentDefinitionName: '',
                name: '',
                uri: '',
            },
        }
    );

export const useChangeEnvironmentExpirationDialogContext =
    surfaceContextWithPropertiesHook<ChangeEnvironmentExpirationDialogProperties>(
        ChangeEnvironmentExpirationDialogContext,
        defaultOpenChangeEnvironmentExpirationDialogHandler,
        defaultCloseChangeEnvironmentExpirationDialogHandler,
        {
            environment: {
                environmentType: '',
                catalogName: '',
                environmentDefinitionName: '',
                name: '',
                uri: '',
            },
        }
    );

export const useOpenWindowsDesktopClientDialogContext =
    surfaceContextWithPropertiesHook<OpenWindowsDesktopClientDialogProperties>(
        OpenWindowsDesktopClientDialogContext,
        defaultOpenOpenWindowsDesktopClientDialogHandler,
        defaultCloseOpenWindowsDesktopClientDialogHandler,
        { openInRdpClientUrl: '' }
    );

export const useConfirmationDialogContext = surfaceContextWithPropertiesHook<ConfirmationDialogProperties>(
    ConfirmationDialogContext,
    defaultOpenConfirmationDialogHandler,
    defaultCloseConfirmationDialogHandler,
    {
        onPrimaryButtonSubmitted: defaultOnSubmit,
        primaryButtonText: '',
        subText: '',
        title: '',
    }
);

export const useHibernatePreviewDialogContext = surfaceContextWithPropertiesHook<HibernatePreviewDialogProperties>(
    HibernatePreviewDialogContext,
    defaultOpenHibernatePreviewDialogHandler,
    defaultCloseHibernatePreviewDialogHandler,
    { devBoxName: '', onHibernateSubmitted: () => void {} }
);

export const useRepairDialogContext = surfaceContextWithPropertiesHook<RepairDialogProperties>(
    RepairDialogContext,
    defaultOpenRepairDialogHandler,
    defaultCloseRepairDialogHandler,
    { onRepairSubmitted: () => void {} }
);

export const useConnectViaAppDialogContext = surfaceContextWithPropertiesHook<OpenConnectViaAppDialogProperties>(
    ConnectViaAppDialogContext,
    defaultOpenConnectViaAppDialogHandler,
    defaultCloseConnectViaAppDialogHandler,
    { cloudPcConnectionUrl: '', remoteDesktopUrl: '' }
);
